/**
 * FamilyGoalsPage - Overview of goals across all children
 * BP-06-03: Care Team & Goals Frontend
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Target,
  Plus,
  Search,
  Filter,
  RefreshCw,
  ChevronDown,
  X,
  TrendingUp,
  CheckCircle2,
  User,
  Calendar,
  BarChart2,
} from 'lucide-react';
import { useFamilyContext } from '@/features/family/hooks/useFamilyContext';
import { NoFamilyState } from '@/features/family/components/NoFamilyState';
import { useChildren } from '@/features/children/hooks/useChildren';
import { useGoals, useGoalsSummary } from '../hooks/useGoals';
import {
  GOAL_TYPE_LABELS,
  GOAL_STATUS_LABELS,
  PRIORITY_LABELS,
  type Goal,
  type GoalStatus,
  type GoalType,
  type GoalPriority,
} from '../types/goalsTypes';

export default function FamilyGoalsPage() {
  const navigate = useNavigate();
  const { familyId, isLoading: isFamilyLoading, hasFamily } = useFamilyContext();

  // Local state
  const [selectedChildId, setSelectedChildId] = useState<string | 'all'>('all');
  const [showChildSelector, setShowChildSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<GoalStatus | null>(null);
  const [typeFilter, setTypeFilter] = useState<GoalType | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<GoalPriority | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');

  // Queries
  const { data: childrenData, isLoading: isLoadingChildren } = useChildren(familyId || '');
  const children = childrenData?.items ?? [];

  // Get goals for selected child (or first child if "all" and need summary)
  const childIdForGoals = selectedChildId === 'all'
    ? children[0]?.id
    : selectedChildId;

  const {
    data: goalsData,
    isLoading: isLoadingGoals,
    error: goalsError,
    refetch: refetchGoals,
  } = useGoals(childIdForGoals || '', {
    status: statusFilter || undefined,
    goalType: typeFilter || undefined,
    priority: priorityFilter || undefined,
    search: searchQuery || undefined,
  });

  const { data: summary } = useGoalsSummary(childIdForGoals || '');

  // Computed
  const goals = goalsData?.items ?? [];
  const selectedChild = children.find((c) => c.id === selectedChildId);

  // Get status color
  const getStatusColor = (status: GoalStatus) => {
    switch (status) {
      case 'achieved':
        return 'bg-green-100 text-green-700';
      case 'in_progress':
        return 'bg-sage/10 text-sage';
      case 'not_started':
        return 'bg-gray-100 text-gray-600';
      case 'discontinued':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  // Get priority badge
  const getPriorityBadge = (priority: GoalPriority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  // Handlers
  const handleChildSelect = (childId: string | 'all') => {
    setSelectedChildId(childId);
    setShowChildSelector(false);
  };

  const handleGoalClick = (goal: Goal) => {
    navigate(`/family/children/${goal.childId}/goals/${goal.id}`);
  };

  const clearFilters = () => {
    setStatusFilter(null);
    setTypeFilter(null);
    setPriorityFilter(null);
    setSearchQuery('');
  };

  // Loading state
  if (isFamilyLoading || isLoadingChildren) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
      </div>
    );
  }

  // No family state
  if (!hasFamily || !familyId) {
    return <NoFamilyState />;
  }

  // No children state
  if (children.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <User className="w-8 h-8 text-sage" />
          </div>
          <h2 className="font-display text-2xl font-semibold text-navy mb-3">
            Add a Child First
          </h2>
          <p className="text-warm-gray mb-6">
            To start tracking goals, you'll need to add a child to your family first.
          </p>
          <Link
            to="/family/children"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Child
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-navy">Goals</h1>
          <p className="text-warm-gray mt-1">
            Track developmental, therapy, and IEP goals
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedChildId !== 'all' && (
            <Link
              to={`/family/children/${selectedChildId}/goals/new`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Goal
            </Link>
          )}
        </div>
      </div>

      {/* Child Selector */}
      <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative">
            <button
              onClick={() => setShowChildSelector(!showChildSelector)}
              className="flex items-center gap-3 px-4 py-2 bg-cream rounded-lg hover:bg-cream-dark transition-colors min-w-[200px]"
            >
              {selectedChildId === 'all' ? (
                <>
                  <div className="w-8 h-8 bg-sage/20 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-sage" />
                  </div>
                  <span className="font-medium text-navy">All Children</span>
                </>
              ) : selectedChild ? (
                <>
                  <div className="w-8 h-8 bg-sage/20 rounded-full flex items-center justify-center">
                    {selectedChild.avatar_url ? (
                      <img
                        src={selectedChild.avatar_url}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-medium text-sage">
                        {selectedChild.first_name[0]}
                      </span>
                    )}
                  </div>
                  <span className="font-medium text-navy">
                    {selectedChild.first_name} {selectedChild.last_name}
                  </span>
                </>
              ) : (
                <>
                  <User className="w-5 h-5 text-warm-gray" />
                  <span className="text-warm-gray">Select a child</span>
                </>
              )}
              <ChevronDown className="w-4 h-4 text-warm-gray ml-auto" />
            </button>

            {showChildSelector && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowChildSelector(false)}
                />
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-warm-gray/10 shadow-lg z-20 overflow-hidden">
                  <button
                    onClick={() => handleChildSelect('all')}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-cream transition-colors ${
                      selectedChildId === 'all' ? 'bg-sage/10' : ''
                    }`}
                  >
                    <div className="w-8 h-8 bg-sage/20 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-sage" />
                    </div>
                    <span className="font-medium text-navy">All Children</span>
                  </button>
                  {children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => handleChildSelect(child.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-cream transition-colors ${
                        selectedChildId === child.id ? 'bg-sage/10' : ''
                      }`}
                    >
                      <div className="w-8 h-8 bg-sage/20 rounded-full flex items-center justify-center">
                        {child.avatar_url ? (
                          <img
                            src={child.avatar_url}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-medium text-sage">
                            {child.first_name[0]}
                          </span>
                        )}
                      </div>
                      <span className="font-medium text-navy">
                        {child.first_name} {child.last_name}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-sage" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-navy">{summary.totalGoals}</p>
                <p className="text-sm text-warm-gray">Total Goals</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-navy">
                  {summary.byStatus?.in_progress ?? 0}
                </p>
                <p className="text-sm text-warm-gray">In Progress</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-navy">
                  {summary.achievedThisMonth ?? 0}
                </p>
                <p className="text-sm text-warm-gray">Achieved This Month</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <BarChart2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-navy">
                  {Math.round(summary.averageProgress ?? 0)}%
                </p>
                <p className="text-sm text-warm-gray">Avg. Progress</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Links per Child */}
      {children.length > 1 && selectedChildId === 'all' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {children.map((child) => (
            <Link
              key={child.id}
              to={`/family/children/${child.id}/goals`}
              className="bg-white rounded-xl p-4 border border-warm-gray/10 hover:border-sage/30 hover:shadow-sm transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-sage/20 rounded-full flex items-center justify-center">
                  {child.avatar_url ? (
                    <img
                      src={child.avatar_url}
                      alt=""
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-lg font-medium text-sage">
                      {child.first_name[0]}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-navy group-hover:text-sage transition-colors">
                    {child.first_name}'s Goals
                  </h3>
                  <p className="text-sm text-warm-gray">
                    View and manage goals
                  </p>
                </div>
                <Target className="w-5 h-5 text-warm-gray group-hover:text-sage transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
            <input
              type="text"
              placeholder="Search goals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-gray hover:text-navy"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg transition-colors ${
                showFilters ? 'bg-sage text-white' : 'text-warm-gray hover:bg-cream'
              }`}
              title="Filters"
            >
              <Filter className="w-5 h-5" />
            </button>
            <button
              onClick={() => refetchGoals()}
              className="p-2 text-warm-gray hover:bg-cream rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <div className="w-px h-6 bg-warm-gray/20" />
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                viewMode === 'cards' ? 'bg-sage text-white' : 'text-warm-gray hover:bg-cream'
              }`}
            >
              Cards
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                viewMode === 'list' ? 'bg-sage text-white' : 'text-warm-gray hover:bg-cream'
              }`}
            >
              List
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-warm-gray/10">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-1">Status</label>
                <select
                  value={statusFilter || ''}
                  onChange={(e) =>
                    setStatusFilter((e.target.value as GoalStatus) || null)
                  }
                  className="w-full px-3 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
                >
                  <option value="">All Statuses</option>
                  {Object.entries(GOAL_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-1">Type</label>
                <select
                  value={typeFilter || ''}
                  onChange={(e) =>
                    setTypeFilter((e.target.value as GoalType) || null)
                  }
                  className="w-full px-3 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
                >
                  <option value="">All Types</option>
                  {Object.entries(GOAL_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-1">Priority</label>
                <select
                  value={priorityFilter || ''}
                  onChange={(e) =>
                    setPriorityFilter((e.target.value as GoalPriority) || null)
                  }
                  className="w-full px-3 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
                >
                  <option value="">All Priorities</option>
                  {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {(statusFilter || typeFilter || priorityFilter) && (
              <button
                onClick={clearFilters}
                className="mt-3 text-sm text-sage hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Goals List */}
      <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
        {isLoadingGoals ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
          </div>
        ) : goalsError ? (
          <div className="text-center py-16">
            <p className="text-red-500">Error loading goals</p>
            <button
              onClick={() => refetchGoals()}
              className="mt-2 text-sage hover:underline"
            >
              Try again
            </button>
          </div>
        ) : goals.length === 0 ? (
          <div className="text-center py-16">
            <Target className="w-12 h-12 text-warm-gray/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-navy mb-2">
              {searchQuery || statusFilter || typeFilter
                ? 'No goals found'
                : 'No goals yet'}
            </h3>
            <p className="text-warm-gray mb-4">
              {searchQuery || statusFilter || typeFilter
                ? 'Try adjusting your search or filters'
                : 'Create goals to track progress and milestones'}
            </p>
            {!searchQuery && !statusFilter && !typeFilter && selectedChildId !== 'all' && (
              <Link
                to={`/family/children/${selectedChildId}/goals/new`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create First Goal
              </Link>
            )}
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {goals.map((goal) => (
              <button
                key={goal.id}
                onClick={() => handleGoalClick(goal)}
                className="bg-cream/50 rounded-xl p-4 text-left hover:bg-cream transition-colors"
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(goal.status)}`}>
                    {GOAL_STATUS_LABELS[goal.status]}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(goal.priority)}`}>
                    {goal.priority}
                  </span>
                </div>
                <h3 className="font-medium text-navy mb-2 line-clamp-2">{goal.title}</h3>
                {goal.description && (
                  <p className="text-sm text-warm-gray mb-3 line-clamp-2">
                    {goal.description}
                  </p>
                )}
                {/* Progress bar */}
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-warm-gray">Progress</span>
                    <span className="font-medium text-navy">{goal.progressPercentage}%</span>
                  </div>
                  <div className="h-2 bg-warm-gray/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-sage rounded-full transition-all"
                      style={{ width: `${goal.progressPercentage}%` }}
                    />
                  </div>
                </div>
                {goal.targetDate && (
                  <div className="flex items-center gap-1 mt-3 text-xs text-warm-gray">
                    <Calendar className="w-3 h-3" />
                    <span>Target: {new Date(goal.targetDate).toLocaleDateString()}</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-warm-gray/10">
            {goals.map((goal) => (
              <button
                key={goal.id}
                onClick={() => handleGoalClick(goal)}
                className="w-full flex items-center gap-4 p-4 hover:bg-cream/50 transition-colors text-left"
              >
                <div className="w-12 h-12 bg-sage/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-sage" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-navy truncate">{goal.title}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(goal.status)}`}>
                      {GOAL_STATUS_LABELS[goal.status]}
                    </span>
                  </div>
                  <p className="text-sm text-warm-gray truncate">
                    {GOAL_TYPE_LABELS[goal.goalType]} • {goal.progressPercentage}% complete
                  </p>
                </div>
                <div className="hidden sm:flex items-center gap-4">
                  <div className="w-24 h-2 bg-warm-gray/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-sage rounded-full"
                      style={{ width: `${goal.progressPercentage}%` }}
                    />
                  </div>
                  {goal.targetDate && (
                    <span className="text-xs text-warm-gray whitespace-nowrap">
                      {new Date(goal.targetDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
